import React, { useState } from 'react';
import { formatCurrency } from '../../utils/calculations';

const ROWS_PER_PAGE = 12;

const AmortizationTable = ({ schedule, calculatorType, dateInfo }) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  if (!schedule || schedule.length === 0) return null;
  
  const totalPages = Math.ceil(schedule.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = Math.min(startIndex + ROWS_PER_PAGE, schedule.length);
  const currentRows = schedule.slice(startIndex, endIndex);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Calculate payment date for a given month
  const getPaymentDate = (monthNumber) => {
    if (!dateInfo || !dateInfo.startDate) return null;
    
    try {
      const startDate = new Date(dateInfo.startDate);
      const paymentDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthNumber, startDate.getDate());
      return paymentDate.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (error) {
      return null;
    }
  };
  
  // Determine columns based on calculator type
  const getColumns = () => {
    const baseColumns = [
      { key: 'month', label: 'Month' }
    ];
    
    // Add date column if date info is provided
    if (dateInfo && dateInfo.startDate) {
      baseColumns.push({ key: 'date', label: 'Payment Date' });
    }
    
    baseColumns.push(
      { key: 'payment', label: 'Payment' },
      { key: 'principal', label: 'Principal' },
      { key: 'interest', label: 'Interest' },
      { key: 'balance', label: 'Balance' },
      { key: 'remainingToRepay', label: 'Remaining to Repay' }
    );
    
    if (calculatorType === 'missed') {
      return [
        ...baseColumns,
        { key: 'missed', label: 'Missed' },
        { key: 'lateFee', label: 'Late Fee' }
      ];
    } else if (calculatorType === 'interest') {
      return [
        ...baseColumns,
        { key: 'interestFrozen', label: 'Interest Frozen' }
      ];
    }
    
    return baseColumns;
  };
  
  const columns = getColumns();
  
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Amortization Schedule</h3>
      
      <div className="overflow-x-auto shadow rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th key={column.key} scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentRows.map((row, index) => (
              <tr key={index} className={row.missed ? 'bg-red-50' : row.interestFrozen ? 'bg-green-50' : ''}>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.month}</td>
                
                {dateInfo && dateInfo.startDate && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {getPaymentDate(row.month)}
                  </td>
                )}
                
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.payment)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.principal)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.balance)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.remainingToRepay)}</td>
                
                {calculatorType === 'missed' && (
                  <>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {row.missed ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {row.lateFee && row.lateFee > 0 ? formatCurrency(row.lateFee) : '-'}
                    </td>
                  </>
                )}
                
                {calculatorType === 'interest' && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {row.interestFrozen ? 'Yes' : 'No'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
            <span className="font-medium">{endIndex}</span> of{' '}
            <span className="font-medium">{schedule.length}</span> payments
          </div>
          
          <nav className="flex space-x-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 text-sm rounded-md ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200 cursor-pointer'
              }`}
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Show pages around current page
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 text-sm rounded-md cursor-pointer ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 text-sm rounded-md ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200 cursor-pointer'
              }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AmortizationTable;